import { IconChevronRight, IconDeviceFloppy } from '@tabler/icons-react';
import React, { useEffect, useState } from 'react';
import { Button, Group, Modal, Tabs } from '@mantine/core';
import { useDispatch, useSelector } from 'react-redux';
import useFetch from '../../helpers/useFetch';
import { setDomain } from '../../store/domainSlice';
import ErrorMessage from '../ErrorMessage';
import Spinner from '../Spinner';
import ReviewSubscription from './ReviewSubscription';
import SubscriptionConfirmationUrlModal from './SubscriptionConfirmationUrlModal';
import SuggestSubscription from './SuggestSubscription';

const tabs = ['select', 'review'];

const ModalWrapper = ({ show, close, children }) => {
  return (
    <Modal size="lg" opened={show} onClose={close} title="Upgrade subscription">
      {children}
    </Modal>
  );
};

const SuggestSubscriptionModal = ({ show, close }) => {
  const { requestedActionToUnlock } = useSelector((state) => state.subscription);
  const { domain } = useSelector((state) => state.domain);
  const dispatch = useDispatch();

  const [tab, setTab] = useState(tabs[0]);
  const [subscriptions, setSubscriptions] = useState();
  const [loadError, setLoadError] = useState();
  const [userError, setUserError] = useState();
  const [loading, setLoading] = useState();
  const [confirmationUrl, setConfirmationUrl] = useState();
  const [newSubscriptions, setNewSubscriptions] = useState([]);

  const domainSubscriptions =
    domain.domainSubscriptions.filter((s) => s.status === 'active').map((s) => s.appliedValues) || [];
  const domainSubscriptionType = domainSubscriptions[0]?.type || 'flagship';

  const fetch = useFetch();

  useEffect(() => {
    fetch(`/api/admin/subscriptions`)
      .then(({ subscriptions }) => {
        setSubscriptions(subscriptions);
      })
      .catch(setLoadError);
  }, [fetch]);

  const cancelButton = (
    <Button variant="default" onClick={close}>
      Cancel
    </Button>
  );

  if (loadError) {
    return (
      <ModalWrapper show={show} close={close}>
        <ErrorMessage message={loadError?.message} />
        <Group justify="flex-end" gap="xs" py="md">{cancelButton}</Group>
      </ModalWrapper>
    );
  }

  if (!subscriptions) {
    return (
      <ModalWrapper show={show} close={close}>
        <Spinner />
        <Group justify="flex-end" gap="xs" py="md">{cancelButton}</Group>
      </ModalWrapper>
    );
  }

  const updateSubscriptions = () => {
    setLoading(true);
    setUserError(undefined);
    let updatedSubscriptions;
    if (domainSubscriptionType === 'flagship') {
      updatedSubscriptions = newSubscriptions;
    } else {
      updatedSubscriptions = [...domainSubscriptions, ...newSubscriptions];
    }

    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/subscriptions/confirmations`, {
      method: 'POST',
      body: JSON.stringify({ selectedSubscriptions: updatedSubscriptions }),
    })
      .then(({ domainSubscriptions, confirmationUrl }) => {
        dispatch(setDomain({ ...domain, domainSubscriptions }));
        if (confirmationUrl) {
          setConfirmationUrl(confirmationUrl);
        }

        close();
      })
      .catch(setUserError)
      .finally(() => setLoading(false));
  };

  const reviewButton = (
    <Button
      onClick={() => {
        setTab(tabs[1]);
        window.scrollTo(0, 0);
      }}
      disabled={!newSubscriptions.length}
    >
      Review <IconChevronRight size={14} />
    </Button>
  );

  const confirmButton = (
    <Button disabled={loading} onClick={updateSubscriptions}>
      Save <IconDeviceFloppy size={14} />
    </Button>
  );

  return (
    <ModalWrapper show={show} close={close}>
      <Tabs value={tab} onChange={setTab}>
        <Tabs.List>
          <Tabs.Tab value="select">Select</Tabs.Tab>
          <Tabs.Tab value="review" disabled={!newSubscriptions.length}>Review</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="select" pt={0}>
          <SuggestSubscription
            subscriptions={subscriptions}
            newSubscriptions={newSubscriptions}
            setNewSubscriptions={setNewSubscriptions}
            requestedActionToUnlock={requestedActionToUnlock}
            domainSubscriptionType={domainSubscriptionType}
          />
        </Tabs.Panel>
        <Tabs.Panel value="review" pt={0}>
          <ReviewSubscription
            domainSubscriptions={domainSubscriptions}
            newSubscriptions={newSubscriptions}
            domainSubscriptionType={domainSubscriptionType}
          />
        </Tabs.Panel>
      </Tabs>
      <Group justify="flex-end" gap="xs" py="md">
        <ErrorMessage message={userError?.message} />
        {cancelButton}
        {tab === 'review' ? confirmButton : reviewButton}
      </Group>
      <SubscriptionConfirmationUrlModal confirmationUrl={confirmationUrl} setConfirmationUrl={setConfirmationUrl} />
    </ModalWrapper>
  );
};

export default SuggestSubscriptionModal;
